import i18next from 'i18next';

import {GetRoutes} from '../GET/GetRoutes';

import jobPostingApplicantsMock from './mocks/job-posting-applicants-mock.json';
import SurveySerializer from '../../../features/company/culture/surveys/serializers/SurveysSerializers';
import {StartSurveyPayload} from '../../../features/company/culture/surveys/types/SurveyTypes';
import CompanyBenefitsSerializers from '../../../features/company/profile/serializers/CompanyBenefitsSerializers';
import CompanyLocationsSerializers from '../../../features/company/profile/serializers/CompanyLocationsSerializers';
import CompanyMediaSerializers from '../../../features/company/profile/serializers/CompanyMediaSerializers';
import CompanyProfileSerializer from '../../../features/company/profile/serializers/CompanyProfileSerializers';
import {CompanyProfile} from '../../../features/company/profile/types';
import JobPostingsSerializer from '../../../features/company/recruiting/job-postings/serializers/JobPostingSerializers';
import {JobPostingFormData} from '../../../features/company/recruiting/job-postings/types/JobPostingsTypes';
import {getUserAuthToken} from '../../../services/auth-service';

interface Args {
  method: 'POST' | 'PATCH' | 'PUT' | 'GET' | 'DELETE';
  isProtected: boolean;
  body?: object;
  contentType?: string;
}

export const generateFetchOptions = ({method, isProtected, body, contentType}: Args): RequestInit => {
  const isLocalEnv = process.env.REACT_APP_ENV === 'local';
  const headers: HeadersInit = {
    'X-Application-Id': process.env.REACT_APP_PROJECT_ID ?? '',
    'Content-Type': contentType ?? 'application/json',
    'Accept-Language': i18next.language,
  };

  if (isProtected) {
    headers.Authorization = `Bearer ${getUserAuthToken()}`;
  }

  if (isLocalEnv && process.env.REACT_APP_API_KEY) {
    headers['X-Application-Secret'] = process.env.REACT_APP_API_KEY;
  }

  return {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  };
};
// POST users/sign_up
export async function createUser(userObject: any): Promise<Response> {
  const options = generateFetchOptions({method: 'POST', isProtected: false, body: userObject});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/users/sign_up`, options);
}

// GET users/me
export async function getMe(): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/users/me`, options);
}

// ------  Company Profile ------

// GET  /api/v1/companies/:company_id/profile
export async function getCompanyProfile(companyId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}`, options);
}

// PATCH /api/v1/companies/:company_id/profile
export async function updateCompanyProfile(profile: CompanyProfile): Promise<Response> {
  const requestBody = CompanyProfileSerializer.toJsonRequest(profile);
  const options = generateFetchOptions({method: 'PATCH', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${profile.resourceId}`, options);
}

// PATCH /api/v1/companies/:company_id/logo
export async function updateCompanyLogo(companyId: string, logoId: string | null): Promise<Response> {
  const requestBody = {
    company_logo: {
      logo_id: logoId,
    },
  };
  const options = generateFetchOptions({method: 'PATCH', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/logo`, options);
}

// GET /api/v1/companies/:id/benefits
export async function getCompanyBenefits(companyId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/benefits`, options);
}

// PATCH /api/v1/companies/:id/benefits
export async function updateCompanyBenefits(
  companyResourceId: string,
  benefits: CompanyProfile['benefits']
): Promise<Response> {
  const requestBody = CompanyBenefitsSerializers.toJSON({benefits});

  const options = generateFetchOptions({method: 'PATCH', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyResourceId}/benefits`, options);
}

// POST /api/v1/companies/:id/benefits
export async function createCompanyBenefits(
  companyResourceId: string,
  benefits: CompanyProfile['benefits']
): Promise<Response> {
  const requestBody = CompanyBenefitsSerializers.toJSON({benefits});

  const options = generateFetchOptions({method: 'POST', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyResourceId}/benefits`, options);
}

// GET /api/v1/companies/:id/locations
export async function getCompanyLocations(companyId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/locations`, options);
}

// PATCH /api/v1/companies/:id/locations
export async function updateCompanyLocations(
  companyResourceId: string,
  locations: CompanyProfile['locations']
): Promise<Response> {
  const requestBody = CompanyLocationsSerializers.toJSON({locations});

  const options = generateFetchOptions({method: 'PATCH', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyResourceId}/locations`, options);
}

// POST /api/v1/companies/:id/locations
export async function createCompanyLocations(
  companyResourceId: string,
  locations: CompanyProfile['locations']
): Promise<Response> {
  const requestBody = CompanyLocationsSerializers.toJSON({locations});

  const options = generateFetchOptions({method: 'POST', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyResourceId}/locations`, options);
}

// DELETE /companies/:company_id/locations
export async function deleteCompanyLocations(
  companyResourceId: string,
  locationResourceIds: string[]
): Promise<Response> {
  const options = generateFetchOptions({method: 'DELETE', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyResourceId}/locations`, {
    ...options,
    body: JSON.stringify({company_locations: locationResourceIds}),
  });
}

// PATCH /api/v1/companies/:id/media_files
export async function updateCompanyMedia(companyResourceId: string, companyMediaFiles: any): Promise<Response> {
  const requestBody = {
    company_media_files: [
      {
        resource_id: companyMediaFiles.resourceId,
        source_url: companyMediaFiles.sourceUrl,
      },
    ],
  };

  const options = generateFetchOptions({method: 'PATCH', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyResourceId}/media_files`, options);
}

// POST /api/v1/companies/:id/media_files
export async function createCompanyMedia(
  companyResourceId: string,
  companyMediaFiles: CompanyProfile['companyMediaFiles']
): Promise<Response> {
  const requestBody = CompanyMediaSerializers.toJSON({companyMediaFiles});

  const options = generateFetchOptions({method: 'POST', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyResourceId}/media_files`, options);
}

// DELETE /api/v1/companies/:id/media_files
export async function deleteCompanyMedia(companyResourceId: string, mediaResourceIds: string[]): Promise<Response> {
  const options = generateFetchOptions({method: 'DELETE', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyResourceId}/media_files`, {
    ...options,
    body: JSON.stringify({company_media_files: mediaResourceIds}),
  });
}

// GET /api/v1/companies/:company_id/departments
export async function getCompanyDepartments(companyId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/departments`, options);
}

// ------  Job Postings ------

// GET /companies/:company_id/job_postings
export async function getJobPostings(companyId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/job_postings`, options);
}

export async function getJobPostingById(companyId: string, jobPostingId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/job_postings/${jobPostingId}`, options);
}

// GET /companies/:company_id/job_postings
export async function getJobPostingsMock(): Promise<Response> {
  return Promise.resolve(new Response(JSON.stringify(jobPostingApplicantsMock), {status: 200}));
}

// POST /companies/:company_id/job_postings
export async function createJobPosting(companyId: string, jobPostingFormData: JobPostingFormData): Promise<Response> {
  const requestBody = JobPostingsSerializer.toPayloadJSON(jobPostingFormData);

  const options = generateFetchOptions({method: 'POST', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/job_postings`, options);
}

// PATCH /companies/:company_id/job_postings/:job_posting_id/publish
export async function publishJobPosting(companyId: string, jobPostingId: string): Promise<Response> {
  const requestBody = {
    company_job_posting: {
      published_at: new Date().toISOString(),
    },
  };

  const options = generateFetchOptions({method: 'PATCH', isProtected: true, body: requestBody});
  return fetch(
    `${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/job_postings/${jobPostingId}/publish`,
    options
  );
}

// PATCH /companies/:company_id/job_postings/:job_posting_id
export async function updateJobPosting(
  companyId: string,
  jobPostingId: string,
  jobPostingFormData: JobPostingFormData
): Promise<Response> {
  const requestBody = JobPostingsSerializer.toPayloadJSON(jobPostingFormData, true);

  const options = generateFetchOptions({method: 'PATCH', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/job_postings/${jobPostingId}`, options);
}

// DELETE /companies/:company_id/job_postings/:job_posting_id
export async function deleteJobPosting(companyId: string, jobPostingId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'DELETE', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/job_postings/${jobPostingId}`, options);
}

// PATCH /companies/:company_id/job_postings/:job_posting_id/archive
export async function archiveJobPosting(companyId: string, jobPostingId: string): Promise<Response> {
  const requestBody = {
    company_job_posting: {
      archived_at: new Date().toISOString(),
    },
  };

  const options = generateFetchOptions({method: 'PATCH', isProtected: true, body: requestBody});
  return fetch(
    `${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/job_postings/${jobPostingId}/archive`,
    options
  );
}

// ------  Company Surveys  ------

// GET /company_surveys/:survey_code --> Get company survey status by survey code (public)
export async function getCompanySurveyStatus(surveyCode: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/company_surveys/${surveyCode}`, options);
}

// GET /companies/:company_id/surveys
export async function getCompanySurveys(companyId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/surveys`, options);
}

// POST /company/:company_id/surveys
export async function createCompanySurvey(companyId: string, surveyPayload: StartSurveyPayload): Promise<Response> {
  const requestBody = SurveySerializer.toJSONPayloadCreate(surveyPayload);

  const options = generateFetchOptions({method: 'POST', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/surveys`, options);
}

// PATCH /companies/:company_id/surveys/:survey_id
export async function updateCompanySurvey(
  companyId: string,
  surveyId: string,
  surveyPayload: StartSurveyPayload
): Promise<Response> {
  const requestBody = SurveySerializer.toJSONPayloadCreate(surveyPayload);

  const options = generateFetchOptions({method: 'PATCH', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/surveys/${surveyId}`, options);
}

// PATCH /companies/:company_id/surveys/:survey_id
export async function completeCompanySurvey(companyId: string, surveyId: string): Promise<Response> {
  const requestBody = {
    company_survey: {
      ended_at: new Date().toISOString(),
    },
  };

  const options = generateFetchOptions({method: 'PATCH', isProtected: true, body: requestBody});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/surveys/${surveyId}`, options);
}

// ------  Company Culture Dashboard ------

export async function getStandardSkills(params?: {
  occupationResourceId?: string;
  skillCategory?: string;
}): Promise<Response> {
  const query = new URLSearchParams();
  if (params?.occupationResourceId) {
    query.set('occupation_resource_id', params.occupationResourceId);
  }
  if (params?.skillCategory) {
    query.set('skill_category', params.skillCategory);
  }
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/standard_skills${suffix}`, options);
}

export async function toggleActionItems(id: string): Promise<Response> {
  const options = generateFetchOptions({
    method: 'PATCH',
    isProtected: true,
  });
  return fetch(
    `${process.env.REACT_APP_API_HOST}/api/v1/cultural_analysis_recommended_action_items/${id}/toggle_completion`,
    options
  );
}

// GET /api/v1/companies/:id/matches?page=1&per_page=5&status=accepted,contacted&company_job_posting_ids=...
export const getPaginatedMatches = async (url: string) => {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/${url}`, options);
};

// ------  Company Applicants Page ------

// GET /companies/:company_id/matches/:id
export async function getCompanyMatch(companyId: string, matchId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/matches/${matchId}`, options);
}

// GET /api/v1/companies/:id/job_postings_summary
export async function getJobPostingsSummary(companyId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/job_postings_summary`, options);
}

// GET /companies/:company_id/job_postings/:job_posting_id/matches
export async function getJobPostingMatches(companyId: string, jobPostingId: string): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(
    `${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/job_postings/${jobPostingId}/matches`,
    options
  );
}

export async function getDepartmentCategoryOptions(): Promise<Response> {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(`${process.env.REACT_APP_API_HOST}/api/v1/config/method_department_categories`, options);
}

// ------  Company Recruiting Dashboard ------

export const getTotalMatchesCount = async (companyId: string) => {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(
    `${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/recruiting_dashboard/hiring_pipeline`,
    options
  );
};

export const getPipelineHistory = async (companyId: string, year?: number) => {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(
    `${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/recruiting_dashboard/pipeline_history${
      year ? `?year=${year}` : ''
    }`,
    options
  );
};

export const getRejectionRates = async (companyId: string) => {
  const options = generateFetchOptions({method: 'GET', isProtected: true});
  return fetch(
    `${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/recruiting_dashboard/rejections`,
    options
  );
};

// GET /companies/:company_id/matches/:match_id/download_pdf_profile
export async function downloadApplicantPdfProfile(companyId: string, matchId: string): Promise<Blob> {
  const options = generateFetchOptions({method: 'GET', isProtected: true, contentType: 'application/pdf'});

  const response = await fetch(
    `${process.env.REACT_APP_API_HOST}/api/v1/companies/${companyId}/matches/${matchId}/download_pdf_profile`,
    options
  );

  if (!response.ok) {
    throw new Error(`Failed to download PDF: ${response.statusText}`);
  }

  const blob = await response.blob();
  return blob;
}

// GET /companies/:company_id/workforce_profiles/:id/download_analysis_pdf
export async function downloadWorkforceProfileAnalysisPdf(companyId: string, profileId: string): Promise<Blob> {
  const options = generateFetchOptions({method: 'GET', isProtected: true, contentType: 'application/pdf'});

  const response = await fetch(
    `${process.env.REACT_APP_API_HOST}${GetRoutes.downloadWorkforceProfileAnalysisPdf(companyId, profileId)}`,
    options
  );

  if (!response.ok) {
    throw new Error(`Failed to download PDF: ${response.statusText}`);
  }

  return response.blob();
}
